import { z } from 'zod';
import { AllPaymentProvidersEnum } from '../types/common-tools/providers';
import { BillingIntervalEnum } from '../types/subscriptions/enums';
/**
 * Public Experiences — MVP1 enumerations and shared primitives.
 *
 * Everything in this folder is browser-safe: no `stripe`, `fastify`,
 * `firebase-admin` or `firebase` imports are permitted (enforced by
 * `tests/public-experiences/server-import-guard.test.ts`).
 */
/** Document schema version stamped on every persisted Public Experience record. */
export const PUBLIC_EXPERIENCE_SCHEMA_VERSION = 1;
export const PublicExperienceSchemaVersionSchema = z.literal(PUBLIC_EXPERIENCE_SCHEMA_VERSION);
/**
 * Part 2 (direct charges) document version. A `schemaVersion: 2` record carries
 * a required `executionScope`; a `schemaVersion: 1` record is a platform-owned
 * destination record exactly as contract 1.0.0 froze it. Version is the ONLY
 * legacy discriminator — never the presence or absence of a field.
 */
export const PUBLIC_EXPERIENCE_SCHEMA_VERSION_V2 = 2;
export const PublicExperienceSchemaVersionV2Schema = z.literal(PUBLIC_EXPERIENCE_SCHEMA_VERSION_V2);
/**
 * Experience type. MVP1 ships `FORM` only.
 *
 * Extension seam: `ORDER` (restaurant) and `DONATION` are documented product
 * directions but are intentionally **not** enum members — adding a member here
 * is a breaking contract change that requires a CONTRACT_CHANGELOG entry.
 */
export var PublicExperienceTypeEnum;
(function (PublicExperienceTypeEnum) {
    PublicExperienceTypeEnum["FORM"] = "FORM";
})(PublicExperienceTypeEnum || (PublicExperienceTypeEnum = {}));
/** Authoring surface within FORM; independent of payment/source schema versions. */
export var PublicExperienceFormTypeEnum;
(function (PublicExperienceFormTypeEnum) {
    PublicExperienceFormTypeEnum["NORMAL"] = "NORMAL";
    PublicExperienceFormTypeEnum["JOTFORM"] = "JOTFORM";
})(PublicExperienceFormTypeEnum || (PublicExperienceFormTypeEnum = {}));
export const PublicExperienceFormTypeSchema = z.nativeEnum(PublicExperienceFormTypeEnum);
export const PublicExperienceTypeSchema = z.nativeEnum(PublicExperienceTypeEnum);
/**
 * Rendering template. MVP1 ships the fixed four-field contact form only
 * (`firstName`, `lastName`, `email`, `phone`).
 */
export var PublicExperienceTemplateEnum;
(function (PublicExperienceTemplateEnum) {
    PublicExperienceTemplateEnum["CONTACT_FORM_V1"] = "CONTACT_FORM_V1";
})(PublicExperienceTemplateEnum || (PublicExperienceTemplateEnum = {}));
export const PublicExperienceTemplateSchema = z.nativeEnum(PublicExperienceTemplateEnum);
/** Authoring lifecycle of an experience. */
export var PublicExperienceStatusEnum;
(function (PublicExperienceStatusEnum) {
    /** Never published. No public projection document exists. */
    PublicExperienceStatusEnum["DRAFT"] = "DRAFT";
    /** Publicly reachable; new checkouts allowed. */
    PublicExperienceStatusEnum["PUBLISHED"] = "PUBLISHED";
    /**
     * Publicly reachable but closed. New checkout creation is rejected
     * IMMEDIATELY with `PXE-002`. Checkouts already created are unaffected: their
     * provider sessions may still be completed within the <=60 min session
     * expiry, `GET` status keeps serving them, webhooks still confirm them, and
     * existing subscriptions continue to bill untouched.
     */
    PublicExperienceStatusEnum["DISABLED"] = "DISABLED";
})(PublicExperienceStatusEnum || (PublicExperienceStatusEnum = {}));
export const PublicExperienceStatusSchema = z.nativeEnum(PublicExperienceStatusEnum);
/**
 * Local checkout state machine. These are PointCraft states, **not** a copy of
 * Stripe Checkout Session or Subscription states.
 */
export var PublicCheckoutStatusEnum;
(function (PublicCheckoutStatusEnum) {
    /** Claim row written, provider session not yet created. */
    PublicCheckoutStatusEnum["INITIALIZING"] = "INITIALIZING";
    /** Provider session exists; a client secret may be issued. */
    PublicCheckoutStatusEnum["PENDING_PAYMENT"] = "PENDING_PAYMENT";
    /** Provider reported checkout completion; funds NOT yet verified. */
    PublicCheckoutStatusEnum["PROCESSING"] = "PROCESSING";
    /** Verified recurring setup; no money collected at signup. Polling complete. */
    PublicCheckoutStatusEnum["BILLING_SCHEDULED"] = "BILLING_SCHEDULED";
    /** Server-verified first payment for the intended amount/account. Terminal. */
    PublicCheckoutStatusEnum["CONFIRMED"] = "CONFIRMED";
    /** Provider session expired before completion. Terminal. */
    PublicCheckoutStatusEnum["EXPIRED"] = "EXPIRED";
    /** Unrecoverable failure for this attempt. Terminal. */
    PublicCheckoutStatusEnum["FAILED"] = "FAILED";
})(PublicCheckoutStatusEnum || (PublicCheckoutStatusEnum = {}));
export const PublicCheckoutStatusSchema = z.nativeEnum(PublicCheckoutStatusEnum);
/** Local workflow outcome of the form submission itself. */
export var SubmissionWorkflowStatusEnum;
(function (SubmissionWorkflowStatusEnum) {
    /** Submitted, first payment not yet verified. */
    SubmissionWorkflowStatusEnum["PENDING"] = "PENDING";
    /** Signup complete with verified future billing, without an initial payment. */
    SubmissionWorkflowStatusEnum["BILLING_SCHEDULED"] = "BILLING_SCHEDULED";
    /** First payment verified once. Terminal for the signup workflow. */
    SubmissionWorkflowStatusEnum["CONFIRMED"] = "CONFIRMED";
    /** Checkout expired or failed without a verified first payment. */
    SubmissionWorkflowStatusEnum["ABANDONED"] = "ABANDONED";
})(SubmissionWorkflowStatusEnum || (SubmissionWorkflowStatusEnum = {}));
export const SubmissionWorkflowStatusSchema = z.nativeEnum(SubmissionWorkflowStatusEnum);
/**
 * Supported checkout origins. Each source has its own strict instruction;
 * adding a kind does not widen recurring form or customer-linkage documents.
 */
export var CheckoutSourceKindEnum;
(function (CheckoutSourceKindEnum) {
    CheckoutSourceKindEnum["PUBLIC_EXPERIENCE_FORM"] = "PUBLIC_EXPERIENCE_FORM";
    CheckoutSourceKindEnum["PAYMENT_LINK"] = "PAYMENT_LINK";
})(CheckoutSourceKindEnum || (CheckoutSourceKindEnum = {}));
export const CheckoutSourceKindSchema = z.nativeEnum(CheckoutSourceKindEnum);
/**
 * The billing shape a checkout asks the provider for. The instruction union
 * binds RECURRING to forms and ONE_TIME to fixed-amount payment requests.
 */
export var CheckoutModeEnum;
(function (CheckoutModeEnum) {
    CheckoutModeEnum["RECURRING"] = "RECURRING";
    CheckoutModeEnum["ONE_TIME"] = "ONE_TIME";
})(CheckoutModeEnum || (CheckoutModeEnum = {}));
export const CheckoutModeSchema = z.nativeEnum(CheckoutModeEnum);
/** Admin-only cancellation semantics. `AT_PERIOD_END` is the default. */
export var SubscriptionCancelModeEnum;
(function (SubscriptionCancelModeEnum) {
    SubscriptionCancelModeEnum["AT_PERIOD_END"] = "AT_PERIOD_END";
    SubscriptionCancelModeEnum["IMMEDIATE"] = "IMMEDIATE";
})(SubscriptionCancelModeEnum || (SubscriptionCancelModeEnum = {}));
export const SubscriptionCancelModeSchema = z.nativeEnum(SubscriptionCancelModeEnum);
/**
 * Payment providers that can back a Public Experience offer.
 *
 * Reuses the existing `AllPaymentProvidersEnum`; MVP1 supports Stripe only.
 * A business whose online provider is anything else gets `PXE-006
 * PROVIDER_UNSUPPORTED`.
 */
export const PUBLIC_EXPERIENCE_SUPPORTED_PROVIDERS = [
    AllPaymentProvidersEnum.STRIPE,
];
export const PublicExperienceProviderSchema = z.literal(AllPaymentProvidersEnum.STRIPE);
/**
 * Recurrence intervals accepted by the MVP1 admin API.
 *
 * The persisted/transport schemas reuse the full `BillingIntervalEnum`
 * (DAILY/WEEKLY/MONTHLY/YEARLY) so that widening later needs no schema change
 * and already-stored documents keep parsing. Admin create/update requests add a
 * runtime refinement restricting input to this list. Widening this list keeps
 * the existing offer wire format and stored recurrence values intact.
 */
// Keep MONTHLY first: existing authoring clients use the first entry as the
// default for a new form. Widening support must not change that default.
export const MVP1_SUPPORTED_INTERVALS = [
    BillingIntervalEnum.MONTHLY,
    BillingIntervalEnum.WEEKLY,
    BillingIntervalEnum.YEARLY,
];
/**
 * Recurrence multiplier accepted by the admin API: one week, month, or year.
 * The schema accepts 1..52 so a widened offer needs no schema
 * change; the admin refinement restricts input to this value.
 */
export const MVP1_SUPPORTED_INTERVAL_COUNT = 1;
/** Currency supported in MVP1. See `PublicExperienceCurrencySchema`. */
export const MVP1_SUPPORTED_CURRENCIES = ['USD'];
